import { useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

// Breakpoints are screen-size based so the same UI can adapt on native
// tablets, foldables, desktop web, and small handsets.
export const BREAKPOINT_MEDIUM = 640;
export const BREAKPOINT_WIDE = 960;
export const TINY_MAX_WIDTH = 380;
export const NARROW_MAX_WIDTH = 520;
export const MEDIUM_MAX_WIDTH = 760;
export const WIDE_MAX_WIDTH = 980;
export const HANDSET_MAX_WIDTH = 480;
export const FORCE_MOBILE_LAYOUT = false;

function getWebPhoneHint(width, height) {
  if (Platform.OS !== 'web') return false;

  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const scr = typeof window !== 'undefined' ? window.screen : null;
    const mq = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia.bind(window)
      : null;
    const ua = String(nav?.userAgent || nav?.vendor || '');
    const uaDataLooksPhone = Boolean(nav?.userAgentData?.mobile);
    const uaLooksPhone = /Android.+Mobile|iPhone|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const screenShortSide = scr ? Math.min(scr.width || 0, scr.height || 0) : 0;
    const viewportLongSide = Math.max(width || 0, height || width || 0);
    const viewportShortSide = Math.min(width || 0, height || width || 0);
    const touchCapable = Number(nav?.maxTouchPoints || 0) > 0;
    const coarseNoHover = Boolean(mq?.('(pointer: coarse)')?.matches && mq?.('(hover: none)')?.matches);
    const narrowViewport = viewportShortSide > 0 && viewportShortSide <= 640 && viewportLongSide <= 1024;

    return uaDataLooksPhone || uaLooksPhone || coarseNoHover || (touchCapable && (
      (screenShortSide > 0 && screenShortSide <= 640) ||
      narrowViewport
    ));
  } catch {
    return false;
  }
}

export function useResponsive() {
  const { width: rawWidth, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const width = Math.max(0, rawWidth || 0);
  const shortSide = Math.min(width || 0, height || width || 0);
  const isPhoneLike =
    FORCE_MOBILE_LAYOUT ||
    (width > 0 && shortSide > 0 && shortSide <= 600) ||
    getWebPhoneHint(width, height);
  const isTiny = width > 0 && width <= TINY_MAX_WIDTH;
  const isNarrow = isPhoneLike || width < BREAKPOINT_MEDIUM;
  const isMedium = !isPhoneLike && width >= BREAKPOINT_MEDIUM && width < BREAKPOINT_WIDE;
  const isWide = !isPhoneLike && width >= BREAKPOINT_WIDE;
  const isWebPhone = Platform.OS === 'web' && isPhoneLike;
  const maxWidth = isWebPhone
    ? undefined
    : isPhoneLike
      ? HANDSET_MAX_WIDTH
      : isWide
        ? WIDE_MAX_WIDTH
        : isMedium
          ? MEDIUM_MAX_WIDTH
          : undefined;
  const contentPadding = isPhoneLike ? (isTiny ? 12 : 14) : isNarrow ? 16 : isMedium ? 20 : 24;
  const bottomInset = Math.max(insets.bottom || 0, Platform.OS === 'web' && isPhoneLike ? 10 : 0);
  const topInset = Math.max(insets.top || 0, 0);

  const contentWidthStyle = useMemo(
    () => ({
      width: '100%',
      ...(maxWidth ? { maxWidth } : null),
      alignSelf: 'center',
    }),
    [maxWidth]
  );

  return {
    bottomInset,
    contentPadding,
    contentWidthStyle,
    height,
    isMedium,
    isNarrow,
    isPhoneLike,
    isTiny,
    isWide,
    maxWidth,
    topInset,
    width,
  };
}
